import { inject } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';

import { FirebaseService } from '@lib/core/services/firebase.service';

import { UploadErrorDialogData, UploadErrorModalComponent } from '../../components/modals/upload-error-dialog/upload-error-modal.component';
import { AiMedicalDocumentItem, DocumentProcessingStatus } from '../models/document.interface';
import { DocumentPreviewService } from '../services/document-preview.service';
import { MedicalDocumentsService } from '../services/medical-documents.service';
import { transformContextDocumentToAiDocument } from '../utils/document-transformers';
import * as MedicalDocumentsActions from './medical-documents.actions';
import { selectUploadingDocuments } from './medical-documents.selectors';

export const getDocumentsListEffect = createEffect(
  (actions$ = inject(Actions), medicalDocumentsService = inject(MedicalDocumentsService)): Observable<Action> =>
    actions$.pipe(
      ofType(MedicalDocumentsActions.getDocumentsList),
      switchMap(({ memberId, page, pageSize, searchTerm }) => {
        return medicalDocumentsService.fetchMemberDocumentsList(memberId, { page, pageSize, searchTerm }).pipe(
          map(response =>
            MedicalDocumentsActions.getDocumentsListSuccess({
              response: {
                value: response.value,
                count: response.count,
                page: response.page,
                pageSize: response.pageSize,
                hasNextPage: response.hasNextPage,
                hasPreviousPage: response.hasPreviousPage,
              },
            })
          ),
          catchError(error => of(MedicalDocumentsActions.getDocumentsListFailure({ error: error.message })))
        );
      })
    ),
  { functional: true }
);

export const uploadDocumentEffect = createEffect(
  (actions$ = inject(Actions), medicalDocumentsService = inject(MedicalDocumentsService)): Observable<Action> =>
    actions$.pipe(
      ofType(MedicalDocumentsActions.uploadDocument),
      switchMap(({ uploadingDocument, userId }) => {
        if (!uploadingDocument || !uploadingDocument.file) {
          return of(
            MedicalDocumentsActions.uploadDocumentFailure({
              error: 'Uploading document or file not found',
              uploadingDocument,
            })
          );
        }

        return medicalDocumentsService.uploadMedicalDocument(userId, uploadingDocument.file).pipe(
          switchMap(uploadedDocument => [
            MedicalDocumentsActions.uploadDocumentSuccess({
              uploadedDocument,
              uploadingDocument,
            }),
          ]),
          catchError(error =>
            of(
              MedicalDocumentsActions.uploadDocumentFailure({
                error: error.message,
                uploadingDocument,
              })
            )
          )
        );
      })
    ),
  { functional: true }
);

export const setCurrentDocumentEffect = createEffect(
  (actions$ = inject(Actions), documentPreviewService = inject(DocumentPreviewService)): Observable<Action> =>
    actions$.pipe(
      ofType(MedicalDocumentsActions.setCurrentDocumentDetails),
      switchMap(({ currentDocument }) => {
        return documentPreviewService.previewDocument(currentDocument, false).pipe(
          map((previewUrl: SafeResourceUrl | null) => MedicalDocumentsActions.setCurrentDocumentDetailsSuccess({ previewUrl })),
          catchError(() => of(MedicalDocumentsActions.setCurrentDocumentDetailsFailure({ error: 'documents.errors.preview' })))
        );
      })
    ),
  { functional: true }
);

export const getAiDocumentsListEffect = createEffect(
  (actions$ = inject(Actions), medicalDocumentsService = inject(MedicalDocumentsService)): Observable<Action> =>
    actions$.pipe(
      ofType(MedicalDocumentsActions.getAiDocumentsList),
      switchMap(({ userId }) => {
        return medicalDocumentsService.getDocumentsByUserId(userId).pipe(
          map(response =>
            MedicalDocumentsActions.getAiDocumentsListSuccess({
              response,
            })
          ),
          catchError(error => of(MedicalDocumentsActions.getAiDocumentsListFailure({ error: error.message })))
        );
      })
    ),
  { functional: true }
);

export const contextSearchDocumentsEffect = createEffect(
  (actions$ = inject(Actions), medicalDocumentsService = inject(MedicalDocumentsService)): Observable<Action> =>
    actions$.pipe(
      ofType(MedicalDocumentsActions.contextSearchDocuments),
      switchMap(({ userId, searchTerm }) => {
        return medicalDocumentsService.contextSearchMedicalRecords(userId, searchTerm).pipe(
          switchMap(response => [
            MedicalDocumentsActions.setAiSearchResponseMessage({ message: response.content }),
            MedicalDocumentsActions.getAiDocumentsListSuccess({
              response: response.documents.map(transformContextDocumentToAiDocument),
            }),
          ]),
          catchError(error => of(MedicalDocumentsActions.getAiDocumentsListFailure({ error: error.message })))
        );
      })
    ),
  { functional: true }
);

export const setCurrentAiDocumentEffect = createEffect(
  (actions$ = inject(Actions), documentPreviewService = inject(DocumentPreviewService), store = inject(Store)): Observable<Action> =>
    actions$.pipe(
      ofType(MedicalDocumentsActions.setCurrentAiDocumentDetails),
      withLatestFrom(store.select(selectUploadingDocuments)),
      switchMap(([{ currentAiDocument }]) => {
        return documentPreviewService.previewDocument(currentAiDocument, true).pipe(
          map((previewUrl: SafeResourceUrl | null) => MedicalDocumentsActions.setCurrentAiDocumentDetailsSuccess({ previewUrl })),
          catchError(() => of(MedicalDocumentsActions.setCurrentAiDocumentDetailsFailure({ error: 'documents.errors.preview' })))
        );
      })
    ),
  { functional: true }
);

export const initFirebaseEventListenerEffect = createEffect(
  (actions$ = inject(Actions), firebaseService = inject(FirebaseService)): Observable<Action> =>
    actions$.pipe(
      ofType(MedicalDocumentsActions.initFirebaseEventListener),
      switchMap(() =>
        firebaseService.getEvents().pipe(
          filter((payload): payload is any => !!(payload && payload.data)),
          map(payload => {
            try {
              // The payload.data already contains the event data
              return MedicalDocumentsActions.handleFirebaseEvent({ event: payload });
            } catch (error) {
              console.error('Error parsing Firebase event:', error);
              return { type: 'NOOP' } as Action;
            }
          }),
          filter(action => action.type !== 'NOOP')
        )
      )
    ),
  { functional: true }
);

export const handleFirebaseEventEffect = createEffect(
  (actions$ = inject(Actions), store = inject(Store)): Observable<Action> =>
    actions$.pipe(
      ofType(MedicalDocumentsActions.handleFirebaseEvent),
      filter(({ event }) => event?.data?.type === 'DocumentProcessingStatusUpdated'),
      withLatestFrom(store.select(selectUploadingDocuments)),
      mergeMap(([{ event }, uploadingDocuments]) => {
        const eventData = event.data;
        const documentId = eventData.documentId;
        const status = eventData.status;
        const actions: Action[] = [];
        let mappedStatus: DocumentProcessingStatus;

        console.log('Firebase event: document processing status changed', event);

        switch (status) {
          case 'InProgress':
            mappedStatus = DocumentProcessingStatus.InProgress;
            break;
          case 'Completed':
            mappedStatus = DocumentProcessingStatus.Done;
            break;
          case 'Failed':
          case 'Warning':
            mappedStatus = DocumentProcessingStatus.Warning;
            break;
          default:
            mappedStatus = DocumentProcessingStatus.InProgress;
        }

        actions.push(
          MedicalDocumentsActions.setDocumentProcessingStatus({
            documentId,
            status: mappedStatus,
          })
        );

        if (mappedStatus === DocumentProcessingStatus.Done) {
          const uploadingDoc = uploadingDocuments.find(doc => doc.uploadResponse?.id === documentId);
          if (uploadingDoc && uploadingDoc.uploadResponse && uploadingDoc.userId) {
            actions.push(
              MedicalDocumentsActions.processDocumentAnalysis({
                documentId,
                analysisData: {
                  ...uploadingDoc.uploadResponse,
                  processingStatus: DocumentProcessingStatus.Done,
                } as AiMedicalDocumentItem,
              })
            );
          }
        }

        if (eventData.errorMessage) {
          // For now, we'll handle this when we get the full document data
          // This is just a status update event
        }

        return actions;
      })
    ),
  { functional: true }
);

export const deleteAiDocumentEffect = createEffect(
  (actions$ = inject(Actions), medicalDocumentsService = inject(MedicalDocumentsService)): Observable<Action> =>
    actions$.pipe(
      ofType(MedicalDocumentsActions.deleteAiDocument),
      switchMap(({ document }) => {
        return medicalDocumentsService.deleteMedicalDocument(document.userId!, document.id).pipe(
          map(() => MedicalDocumentsActions.deleteAiDocumentSuccess({ documentId: document.id })),
          catchError(error => of(MedicalDocumentsActions.deleteAiDocumentFailure({ error: error.message })))
        );
      })
    ),
  { functional: true }
);

export const uploadDocumentFailureEffect = createEffect(
  (actions$ = inject(Actions), dialog = inject(MatDialog)): Observable<Action> =>
    actions$.pipe(
      ofType(MedicalDocumentsActions.uploadDocumentFailure),
      map(({ error }) => {
        const dialogData: UploadErrorDialogData = {
          message: 'Помилка завантаження!',
          additionalText: error || 'Сталася помилка під час завантаження документа.',
        };

        dialog.open(UploadErrorModalComponent, {
          data: dialogData,
          width: '500px',
          disableClose: false,
        });

        return { type: 'NOOP' } as Action;
      })
    ),
  { functional: true }
);
